import { Box, Typography, LinearProgress, Collapse, CircularProgress } from "@mui/material";
import { useContext, useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChatContext } from "../../contexts/chat/context";
import { SQLQuery, DocSearch, ReasoningStep } from "../../contexts/chat/type";

const sanitizeStreamedPreview = (raw: string): string => {
  if (!raw) return "";

  return raw
    .split("\n")
    .map((line) => {
      let normalized = line;

      // Remove horizontal rules
      if (/^\s{0,3}(-{3,}|\*{3,}|_{3,})\s*$/.test(normalized)) return "";

      // Remove code fence markers
      if (/^\s*```/.test(normalized)) return "";

      // Convert markdown table rows/separators into stream-friendly bullets
      if (normalized.includes("|")) {
        const pipeCount = (normalized.match(/\|/g) || []).length;
        if (pipeCount >= 2) {
          if (/^\s*\|?[\s:-]+(\|[\s:-]+)+\|?\s*$/.test(normalized)) return "";
          const cells = normalized
            .split("|")
            .map((c) => c.trim())
            .filter(Boolean);
          if (cells.length > 0) {
            normalized = `- ${cells.join(" - ")}`;
          }
        }
      }

      return normalized.replace(/\s{2,}/g, " ").trimEnd();
    })
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

// Union type for combined events
type AgentEvent =
  | { type: 'sql'; iteration: number; data: SQLQuery }
  | { type: 'doc'; iteration: number; data: DocSearch }
  | { type: 'reasoning'; iteration: number; data: ReasoningStep };

export const AgentThinking = () => {
  const { agentState } = useContext(ChatContext);
  const [showDetails, setShowDetails] = useState(true);

  // Debug logging
  console.log("AgentThinking render - status:", agentState.status);
  console.log("AgentThinking VERSION: WITH_SPINNER");

  // Combine and sort all events by iteration number
  const sortedEvents = useMemo((): AgentEvent[] => {
    const events: AgentEvent[] = [
      ...agentState.sqlQueries.map((q): AgentEvent => ({ type: 'sql', iteration: q.iteration, data: q })),
      ...agentState.docSearches.map((d): AgentEvent => ({ type: 'doc', iteration: d.iteration, data: d })),
      ...agentState.reasoningHistory.map((r): AgentEvent => ({ type: 'reasoning', iteration: r.iteration, data: r })),
    ];
    return events.sort((a, b) => a.iteration - b.iteration);
  }, [agentState.sqlQueries, agentState.docSearches, agentState.reasoningHistory]);

  // Only show when agent is streaming
  if (agentState.status !== "streaming") {
    console.log("AgentThinking - NOT rendering (status is not streaming)");
    return null;
  }

  console.log("AgentThinking - RENDERING (status is streaming)");

  const progress =
    agentState.maxIterations > 0
      ? (agentState.currentIteration / agentState.maxIterations) * 100
      : 0;

  return (
    <Box
      sx={{
        alignSelf: "flex-start",
        m: 1,
        p: 2,
        borderRadius: "8px",
        borderBottomLeftRadius: 0,
        backgroundColor: "white",
        border: "1px solid",
        borderColor: "primary.light",
        maxWidth: "90%",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <CircularProgress size={16} thickness={5} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
          Thinking...
        </Typography>
      </Box>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          mb: 1.5,
          height: 4,
          borderRadius: 2,
          backgroundColor: "grey.200",
          "& .MuiLinearProgress-bar": {
            borderRadius: 2,
          },
        }}
      />

      {/* Current reasoning */}
      {agentState.reasoning && (
        <Box
          sx={{
            backgroundColor: "grey.50",
            p: 2,
            borderRadius: 1,
            mb: 1.5,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: "text.primary",
              fontStyle: "italic",
              fontSize: "0.95rem",
              lineHeight: 1.6,
            }}
          >
            {agentState.reasoning}
          </Typography>
        </Box>
      )}

      {/* Incremental completion text shown as plain text (markdown stripped) */}
      {agentState.streamedText && (
        <Box
          sx={{
            backgroundColor: "white",
            p: 1.5,
            borderRadius: 1,
            mb: 1.5,
            border: "1px solid",
            borderColor: "grey.200",
            "& .stream-markdown": {
              "& p": { m: 0, mb: 0.5, "&:last-child": { mb: 0 } },
              "& ul, & ol": { mt: 0.25, mb: 0.5, pl: 2.5 },
              "& li": { mb: 0.25 },
              "& h1, & h2, & h3, & h4": {
                mt: 0.5,
                mb: 0.4,
                fontWeight: 600,
                lineHeight: 1.3,
              },
              "& h1": { fontSize: "1rem" },
              "& h2": { fontSize: "0.95rem" },
              "& h3, & h4": { fontSize: "0.9rem" },
              "& strong": { fontWeight: 600 },
              "& em": { fontStyle: "italic" },
              "& code": {
                backgroundColor: "rgba(0,0,0,0.06)",
                px: 0.3,
                borderRadius: "3px",
                fontSize: "0.85em",
              },
            },
          }}
        >
          <Box className="stream-markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {sanitizeStreamedPreview(agentState.streamedText)}
            </ReactMarkdown>
          </Box>
        </Box>
      )}

      {/* Activity indicators */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {agentState.docSearches.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              backgroundColor: "secondary.light",
              color: "secondary.dark",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: "0.75rem",
            }}
          >
            <span>📚</span>
            <span>{agentState.docSearches.length} doc search{agentState.docSearches.length > 1 ? "es" : ""}</span>
          </Box>
        )}
        {agentState.sqlQueries.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              backgroundColor: "success.light",
              color: "success.dark",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: "0.75rem",
            }}
          >
            <span>🔍</span>
            <span>{agentState.sqlQueries.length} SQL quer{agentState.sqlQueries.length > 1 ? "ies" : "y"}</span>
          </Box>
        )}
        {agentState.charts.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              backgroundColor: "warning.light",
              color: "warning.dark",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: "0.75rem",
            }}
          >
            <span>📊</span>
            <span>{agentState.charts.length} chart{agentState.charts.length > 1 ? "s" : ""}</span>
          </Box>
        )}
      </Box>

      {/* Expandable details */}
      {(agentState.sqlQueries.length > 0 || agentState.docSearches.length > 0 || agentState.reasoningHistory.length > 0) && (
        <Box sx={{ mt: 1.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: "primary.main",
              cursor: "pointer",
              fontWeight: 500,
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? "Hide details" : "Show details"}
          </Typography>
          <Collapse in={showDetails}>
            <Box sx={{ mt: 1.5, position: "relative", pl: 3 }}>
                <Box
                  sx={{
                    position: "absolute",
                    left: 6,
                    top: 8,
                    bottom: 8,
                    width: 2,
                    backgroundColor: "grey.200",
                  }}
                />
                {sortedEvents.map((event, idx) => {
                  const isLast = idx === sortedEvents.length - 1;
                  const dotColor = event.type === 'sql' ? 'success.main' : event.type === 'doc' ? 'secondary.main' : 'primary.main';

                  if (event.type === 'sql') {
                    const query = event.data;
                    return (
                      <Box
                        key={`sql-${idx}`}
                        sx={{
                          mb: isLast ? 0 : 2,
                          position: "relative",
                          animation: "fadeIn 0.3s ease-out",
                          "@keyframes fadeIn": {
                            from: { opacity: 0, transform: "translateY(-8px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            left: -24,
                            top: 4,
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: dotColor,
                            border: "2px solid white",
                            boxShadow: "0 0 0 2px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Box
                          sx={{
                            backgroundColor: "white",
                            borderRadius: 1,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            overflow: "hidden",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "success.dark", p: 1, pb: 0.5 }}>
                            🔍 SQL Query - {query.row_count} rows
                          </Typography>
                          <Box sx={{ backgroundColor: "grey.900", p: 1.5, overflow: "auto", maxHeight: 120 }}>
                            <Typography
                              component="pre"
                              sx={{ fontSize: "0.85rem", fontFamily: "monospace", color: "grey.100", m: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}
                            >
                              {query.sql || "SQL not available"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  } else if (event.type === 'doc') {
                    const search = event.data;
                    return (
                      <Box
                        key={`doc-${idx}`}
                        sx={{
                          mb: isLast ? 0 : 2,
                          position: "relative",
                          animation: "fadeIn 0.3s ease-out",
                          "@keyframes fadeIn": {
                            from: { opacity: 0, transform: "translateY(-8px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            left: -24,
                            top: 4,
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: dotColor,
                            border: "2px solid white",
                            boxShadow: "0 0 0 2px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Box
                          sx={{
                            backgroundColor: "white",
                            borderRadius: 1,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            p: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "secondary.dark", mb: 0.5 }}>
                            📚 Doc Search
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.primary", fontStyle: "italic" }}>
                            "{search.question}"
                          </Typography>
                          {search.table_names && search.table_names.length > 0 && (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                              {search.table_names.map((table, tableIdx) => (
                                <Box
                                  key={tableIdx}
                                  sx={{
                                    backgroundColor: "secondary.light",
                                    color: "secondary.dark",
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    fontSize: "0.75rem",
                                    fontFamily: "monospace",
                                    fontWeight: 500,
                                  }}
                                >
                                  {table}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  } else {
                    const reasoning = event.data;
                    return (
                      <Box
                        key={`reasoning-${idx}`}
                        sx={{
                          mb: isLast ? 0 : 2,
                          position: "relative",
                          animation: "fadeIn 0.3s ease-out",
                          "@keyframes fadeIn": {
                            from: { opacity: 0, transform: "translateY(-8px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            left: -24,
                            top: 4,
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: dotColor,
                            border: "2px solid white",
                            boxShadow: "0 0 0 2px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Box
                          sx={{
                            backgroundColor: "white",
                            borderRadius: 1,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            p: 1,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                            <CircularProgress size={14} thickness={5} />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "primary.dark" }}
                            >
                              Thinking...
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic", lineHeight: 1.5 }}>
                            {reasoning.text}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }
                })}
              </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};
